import React, { useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { getWalletDownloadLinks, resolveProvider } from '../utils/walletUtils';
import { useLanguage } from '../context/LanguageContext';
import './WalletConnector.css';

const WalletConnector = ({ onClose, onConnected }) => {
  const { connectWallet, isConnecting } = useWeb3();
  const { t } = useLanguage();
  const [installing, setInstalling] = useState(null);
  const [activeWalletId, setActiveWalletId] = useState(null);

  // Always show these main wallet options
  const mainWallets = [
    { id: 'metamask', name: 'MetaMask', icon: '🦊' },
    { id: 'trustwallet', name: 'Trust Wallet', icon: '🔒' },
    { id: 'coinbase', name: 'Coinbase Wallet', icon: '🔵' },
    { id: 'binance', name: 'Binance Wallet', icon: '🟡' },
    { id: 'walletconnect', name: 'WalletConnect', icon: '🔗' },
  ];

  // Check if a wallet is installed
  const isWalletInstalled = (walletId) => {
    if (walletId === 'walletconnect') return true;
    return !!resolveProvider(walletId);
  };

  const handleConnect = async (walletId) => {
    setActiveWalletId(walletId);
    // If wallet not installed, redirect to install
    if (!isWalletInstalled(walletId)) {
      handleInstall(walletId);
      return;
    }

    try {
      const connected = await connectWallet(walletId);
      if (connected) {
        if (onConnected) onConnected();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error('Connection error:', error);
      // If connection fails and wallet seems not installed, redirect to install
      if (error.message?.includes('No wallet provider found') || error.message?.includes('not found')) {
        handleInstall(walletId);
      }
      setActiveWalletId(null);
    }
  };

  const handleInstall = (walletId) => {
    const links = getWalletDownloadLinks();
    const link = links[walletId];
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      setInstalling(walletId);
    }
  };

  return (
    <div className="wallet-connector-overlay" onClick={onClose}>
      <div className="wallet-connector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wallet-connector-header">
          <h2>{t('connectWallet') || 'Connect Wallet'}</h2>
          <button className="wallet-connector-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="wallet-connector-content">
          {activeWalletId === 'walletconnect' ? (
            <div className="walletconnect-view">
              <div className="walletconnect-title">WalletConnect</div>
              <div className="walletconnect-text">
                {isConnecting ? (t('connecting') || 'Connecting...') : 'Scan the QR code with your mobile wallet to continue.'}
              </div>
              <button
                className="walletconnect-back"
                type="button"
                onClick={() => setActiveWalletId(null)}
                disabled={isConnecting}
              >
                Back
              </button>
            </div>
          ) : (
            <>
              <div className="wallet-connector-list">
                {mainWallets.map((wallet) => {
                  const installed = isWalletInstalled(wallet.id);
                  const isInstalling = installing === wallet.id;
                  
                  return (
                    <button
                      key={wallet.id}
                      className="wallet-option-btn"
                      onClick={() => handleConnect(wallet.id)}
                      disabled={isConnecting && !isInstalling}
                    >
                      <span className="wallet-icon">{wallet.icon}</span>
                      <div className="wallet-option-info">
                        <div className="wallet-option-name">
                          {wallet.name}
                          {installed && <span className="wallet-installed-badge">✓</span>}
                        </div>
                        <div className="wallet-option-desc">
                          {isInstalling 
                            ? t('opening') || 'Opening installation page...'
                            : isConnecting 
                            ? t('connecting') || 'Connecting...' 
                            : installed 
                            ? t('clickToConnect') || 'Click to connect'
                            : t('clickToInstall') || 'Click to install'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="wallet-connector-footer">
                <p className="wallet-connector-note">
                  {t('walletSecurityNote') || 'By connecting, you agree to the terms of service and privacy policy.'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletConnector;
